import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../services/api';
import { INK, FONTS, ghostBtnSx } from '../../styles/ledger';
import {
  LedgerPage,
  KPIStrip,
  KPI,
  LedgerCard,
  SectionHeader,
  StatusDot,
  MonoTag,
  HeaderIconBtn,
} from '../../components/admin/LedgerPrimitives';

type AiStatus =
  | 'active' | 'completed' | 'abandoned' | 'failed'
  | 'failed_carrier_timeout' | 'refunding' | 'refunded' | 'refund_failed';

type MessageRole = 'system' | 'user' | 'assistant' | 'tool';

interface AiMessageDetail {
  id: number; role: MessageRole; content: string;
  tool_calls: unknown[] | null; tool_call_id: string | null;
  input_tokens: number; output_tokens: number; model: string;
  latency_ms: number; created_at: string;
}
interface AiChargeIntentDetail {
  id: number; status: string; amount: string; currency: string;
  idempotency_key: string; created_at: string; updated_at: string;
}
interface AiConversationDetail {
  id: number; uuid: string; status: AiStatus;
  order_id: number | null;
  total_input_tokens: number; total_output_tokens: number;
  total_cost_usd: string;
  started_at: string; completed_at: string | null;
  user: { id: number; first_name: string; last_name: string; email: string; company: string | null };
  messages: AiMessageDetail[];
  charge_intents: AiChargeIntentDetail[];
}

const statusTone = (s: string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (s === 'completed' || s === 'refunded' || s === 'settled') return 'active';
  if (s === 'failed' || s === 'refund_failed') return 'inactive';
  if (s === 'active') return 'info';
  if (s === 'refunding' || s === 'failed_carrier_timeout' || s === 'pending') return 'warning';
  return 'neutral';
};

const roleColor = (role: MessageRole): string => {
  switch (role) {
    case 'system': return INK.subtle;
    case 'user': return '#2A5F8C';
    case 'assistant': return INK.accent;
    case 'tool': return INK.warning;
  }
};

const formatDateTime = (dt: string) =>
  new Date(dt).toLocaleString('en-CA', { hour12: false }) + ' UTC';

const AiConversationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: conv, isLoading, isError, refetch } = useQuery<AiConversationDetail>({
    queryKey: ['admin', 'ai', 'conversations', id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/admin/ai/conversations/${id}`);
      return res.data;
    },
    enabled: Boolean(id),
  });

  if (isLoading) {
    return (
      <LedgerPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={28} sx={{ color: INK.accent }} />
        </Box>
      </LedgerPage>
    );
  }

  if (isError || !conv) {
    return (
      <LedgerPage>
        <Alert severity="error" sx={{ borderRadius: 0, fontFamily: FONTS.sans, mb: 2 }}>
          Failed to load conversation.
        </Alert>
        <Button onClick={() => navigate('/admin/ai/conversations')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
          Back
        </Button>
      </LedgerPage>
    );
  }

  return (
    <LedgerPage eyebrowFooter={`Almaship · AI conversation · № ${String(conv.id).padStart(3, '0')}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Button
          onClick={() => navigate('/admin/ai/conversations')}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{ ...ghostBtnSx, border: 'none', color: INK.muted, px: 1, '&:hover': { bgcolor: 'transparent', color: INK.accent, borderColor: 'transparent' } }}
        >
          Back to conversations
        </Button>
        <HeaderIconBtn title="Refresh" onClick={() => refetch()}>
          <RefreshIcon sx={{ fontSize: 18 }} />
        </HeaderIconBtn>
      </Stack>

      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, letterSpacing: '0.18em', color: INK.accent, fontWeight: 500, textTransform: 'uppercase' }}>
            AI agent · Conversation
          </Typography>
        </Stack>
        <Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 44, md: 64 }, lineHeight: 0.95, letterSpacing: '-0.02em', color: INK.text, fontWeight: 400 }}>
          Conversation
          <Box component="span" sx={{ fontFamily: FONTS.mono, fontSize: { xs: 30, md: 42 }, color: INK.accent, ml: 2 }}>
            № {String(conv.id).padStart(3, '0')}
          </Box>
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, letterSpacing: '0.04em' }}>
            {conv.uuid}
          </Typography>
          <StatusDot label={conv.status.replace(/_/g, ' ')} tone={statusTone(conv.status)} />
          {conv.order_id && (
            <Button
              component={Link}
              to={`/admin/orders/${conv.order_id}`}
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
              sx={{ ...ghostBtnSx, py: 0.4, px: 1, fontSize: 11 }}
            >
              Order #{conv.order_id}
            </Button>
          )}
        </Stack>
      </Box>

      <KPIStrip cols={4}>
        <KPI label="User" value={<Typography sx={{ fontFamily: FONTS.serif, fontSize: { xs: 22, md: 28 }, letterSpacing: '-0.01em' }}>{conv.user?.first_name}</Typography>} sub={conv.user?.email} />
        <KPI label="Started" value={new Date(conv.started_at).toLocaleDateString('en-CA')} sub={new Date(conv.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
        <KPI label="Tokens" value={(conv.total_input_tokens + conv.total_output_tokens).toLocaleString()} sub={`in ${conv.total_input_tokens} · out ${conv.total_output_tokens}`} />
        <KPI label="Cost" value={`$${parseFloat(conv.total_cost_usd).toFixed(4)}`} sub="USD" accent />
      </KPIStrip>

      <LedgerCard padded={false} mb={3}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
          <SectionHeader eyebrow="Thread" title={`Messages · ${conv.messages.length}`} />
        </Box>
        {conv.messages.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, color: INK.subtle }}>No messages.</Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {conv.messages.map((msg) => {
              const isSystem = msg.role === 'system';
              const color = roleColor(msg.role);
              return (
                <Box key={msg.id} sx={{ borderBottom: `1px solid ${INK.hairlineSoft}`, '&:last-of-type': { borderBottom: 'none' } }}>
                  <Box sx={{ px: 3, py: 1.25, bgcolor: INK.surfaceTint, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography
                      sx={{
                        fontFamily: FONTS.mono,
                        fontSize: 10,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: color,
                        fontWeight: 600,
                        px: 0.85,
                        py: 0.3,
                        border: `1px solid ${color}`,
                      }}
                    >
                      {msg.role}
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle }}>
                      {formatDateTime(msg.created_at)}
                    </Typography>
                    {msg.model && (
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle }}>
                        · {msg.model}
                      </Typography>
                    )}
                    {(msg.input_tokens > 0 || msg.output_tokens > 0) && (
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle }}>
                        · in {msg.input_tokens} / out {msg.output_tokens}
                      </Typography>
                    )}
                    {msg.latency_ms > 0 && (
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle }}>
                        · {msg.latency_ms}ms
                      </Typography>
                    )}
                  </Box>
                  {isSystem ? (
                    <Accordion
                      elevation={0}
                      disableGutters
                      sx={{
                        bgcolor: 'transparent',
                        '&::before': { display: 'none' },
                        '& .MuiAccordionSummary-root': { px: 3, minHeight: 36 },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: INK.subtle }} />}>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, letterSpacing: '0.04em' }}>
                          System prompt (click to expand)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                        <Box
                          component="pre"
                          sx={{ fontFamily: FONTS.mono, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, color: INK.text, lineHeight: 1.55 }}
                        >
                          {msg.content}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <Box sx={{ px: 3, py: 2 }}>
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: msg.role === 'tool' ? FONTS.mono : FONTS.sans,
                          fontSize: msg.role === 'tool' ? 11.5 : 13.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          m: 0,
                          color: INK.text,
                          lineHeight: 1.55,
                        }}
                      >
                        {msg.content}
                      </Box>
                    </Box>
                  )}
                  {msg.tool_calls && msg.tool_calls.length > 0 && (
                    <Accordion
                      elevation={0}
                      disableGutters
                      sx={{
                        bgcolor: 'transparent',
                        borderTop: `1px solid ${INK.hairlineSoft}`,
                        '&::before': { display: 'none' },
                        '& .MuiAccordionSummary-root': { px: 3, minHeight: 36 },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: INK.subtle }} />}>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, letterSpacing: '0.04em' }}>
                          Tool calls ({msg.tool_calls.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                        <Box
                          component="pre"
                          sx={{ fontFamily: FONTS.mono, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, bgcolor: INK.surfaceTint, p: 1.5, borderRadius: 0.5, border: `1px solid ${INK.hairline}` }}
                        >
                          {JSON.stringify(msg.tool_calls, null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </LedgerCard>

      {conv.charge_intents.length > 0 && (
        <LedgerCard padded={false}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
            <SectionHeader eyebrow="Billing" title={`Charge intents · ${conv.charge_intents.length}`} />
          </Box>
          <Stack divider={<Box sx={{ height: 1, bgcolor: INK.hairlineSoft }} />}>
            {conv.charge_intents.map((intent) => (
              <Stack key={intent.id} direction="row" spacing={3} alignItems="center" flexWrap="wrap" sx={{ px: 3, py: 2 }}>
                <StatusDot label={intent.status} tone={statusTone(intent.status)} />
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13.5, color: INK.text, fontWeight: 600 }}>
                  {intent.currency} {parseFloat(intent.amount).toFixed(2)}
                </Typography>
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                  {formatDateTime(intent.created_at)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <MonoTag>{intent.idempotency_key.slice(0, 16)}…</MonoTag>
              </Stack>
            ))}
          </Stack>
        </LedgerCard>
      )}
    </LedgerPage>
  );
};

export default AiConversationDetailPage;
